/**
 * Task 18 Phase F — Global listener that pops the
 * FileMissingPostDeployModal whenever the axios interceptor broadcasts a
 * 410 file_missing_post_deploy. Tries to infer the customer_id from the
 * failed URL when it carries an obvious one (admin/customers/{id}/... or
 * admin/contracts/{id}/... — we resolve contract → customer via a
 * follow-up GET).
 */
import React, { useEffect, useState } from "react";
import { FileMissingPostDeployModal } from "./FileMissingPostDeployModal";
import api from "@/lib/api";

const CUSTOMER_FROM_URL = /\/admin\/customers\/([0-9a-f-]{36})\b/;
const CONTRACT_FROM_URL = /\/admin\/contracts\/([0-9a-f-]{36})\b/;
const RENTAL_FROM_URL = /\/admin\/rentals\/([0-9a-f-]{36})\b/;
const INVOICE_FROM_URL = /\/admin\/invoices\/([0-9a-f-]{36})\b/;
const CUSTOMER_RENTAL_URL = /\/customer\/rentals\/([0-9a-f-]{36})\b/;
const CUSTOMER_CONTRACT_URL = /\/customer\/contracts\/([0-9a-f-]{36})\b/;

async function inferCustomerId(url) {
    if (!url) return null;
    try {
        let m = url.match(CUSTOMER_FROM_URL);
        if (m) return m[1];
        m = url.match(CONTRACT_FROM_URL);
        if (m) {
            const { data } = await api.get(`/admin/contracts/${m[1]}`);
            return data?.customer_id || null;
        }
        m = url.match(RENTAL_FROM_URL);
        if (m) {
            const { data } = await api.get(`/admin/rentals/${m[1]}`);
            return data?.customer_id || null;
        }
        m = url.match(INVOICE_FROM_URL);
        if (m) {
            const { data } = await api.get(`/admin/invoices/${m[1]}`);
            return data?.customer_id || null;
        }
        // Customer-side surfaces: no customer_id to drive a reupload email
        // (the customer IS the actor). Leave null so the modal shows the
        // close-only variant.
        if (CUSTOMER_RENTAL_URL.test(url) || CUSTOMER_CONTRACT_URL.test(url)) return null;
    } catch { /* swallow — best-effort */ }
    return null;
}

export function FileMissingPostDeployListener() {
    const [state, setState] = useState({ open: false, filename: null, customerId: null });

    useEffect(() => {
        const handler = async (e) => {
            const { filename, url } = e.detail || {};
            const customerId = await inferCustomerId(url);
            setState({ open: true, filename, customerId });
        };
        window.addEventListener("rp:file-missing", handler);
        return () => window.removeEventListener("rp:file-missing", handler);
    }, []);

    return (
        <FileMissingPostDeployModal
            open={state.open}
            onOpenChange={(o) => setState((s) => ({ ...s, open: o }))}
            customerId={state.customerId}
            filename={state.filename}
        />
    );
}

export default FileMissingPostDeployListener;
